import React, { useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { withTranslation } from "react-i18next";
import { withRouter } from "react-router";
import { Card, Table, Alert, Dimmer, Badge, Grid } from "tabler-react";

import HasPermissionWrapper from "../../../../HasPermissionWrapper";
import ClassEditBase from "../ClassEditBase";
import WaitlistEntryRow from "./WaitlistEntryRow";
import WaitlistControls from "./WaitlistControls";

import {
    GET_SHOP_FEATURES_QUERY,
    GET_CLASS_WAITLIST_QUERY,
    PROMOTE_FROM_WAITLIST_MUTATION,
    CANCEL_WAITLIST_MUTATION,
    BULK_PROMOTE_FROM_WAITLIST_MUTATION,
    BULK_CANCEL_WAITLIST_MUTATION,
} from "./queries";

const ClassWaitlistPage = ({ t, match, history }) => {
    const classId = match.params.class_id;
    const [currentDate, setCurrentDate] = useState(
        new Date().toISOString().split("T")[0],
    );

    // Check if waitlist feature is enabled
    const { data: shopFeaturesData, loading: shopFeaturesLoading } = useQuery(
        GET_SHOP_FEATURES_QUERY,
    );
    const waitlistEnabled =
        shopFeaturesData?.systemFeatureShop?.waitlist || false;

    // Get waitlist data
    const {
        data: waitlistData,
        loading: waitlistLoading,
        error: waitlistError,
        refetch: refetchWaitlist,
    } = useQuery(GET_CLASS_WAITLIST_QUERY, {
        variables: {
            scheduleItem: classId,
            date: currentDate,
        },
        skip: !waitlistEnabled,
        pollInterval: 30000, // Refresh every 30 seconds
    });

    // Mutations
    const [promoteFromWaitlist, { loading: promoteLoading }] = useMutation(
        PROMOTE_FROM_WAITLIST_MUTATION,
        {
            refetchQueries: [
                {
                    query: GET_CLASS_WAITLIST_QUERY,
                    variables: { scheduleItem: classId, date: currentDate },
                },
            ],
        },
    );

    const [cancelWaitlist, { loading: cancelLoading }] = useMutation(
        CANCEL_WAITLIST_MUTATION,
        {
            refetchQueries: [
                {
                    query: GET_CLASS_WAITLIST_QUERY,
                    variables: { scheduleItem: classId, date: currentDate },
                },
            ],
        },
    );

    const [bulkPromoteFromWaitlist, { loading: bulkPromoteLoading }] =
        useMutation(BULK_PROMOTE_FROM_WAITLIST_MUTATION, {
            refetchQueries: [
                {
                    query: GET_CLASS_WAITLIST_QUERY,
                    variables: { scheduleItem: classId, date: currentDate },
                },
            ],
        });

    const [bulkCancelWaitlist, { loading: bulkCancelLoading }] = useMutation(
        BULK_CANCEL_WAITLIST_MUTATION,
        {
            refetchQueries: [
                {
                    query: GET_CLASS_WAITLIST_QUERY,
                    variables: { scheduleItem: classId, date: currentDate },
                },
            ],
        },
    );

    const isLoading =
        promoteLoading ||
        cancelLoading ||
        bulkPromoteLoading ||
        bulkCancelLoading;

    const scheduleClass = waitlistData?.scheduleClass;
    const waitlistEntries =
        waitlistData?.classWaitlist?.edges?.map((edge) => edge.node) || [];

    const handlePromote = async (entryId) => {
        await promoteFromWaitlist({
            variables: { scheduleItemAttendance: entryId },
        });
    };

    const handleCancel = async (entryId) => {
        await cancelWaitlist({
            variables: { scheduleItemAttendance: entryId },
        });
    };

    const handleBulkPromote = async (entryIds) => {
        await bulkPromoteFromWaitlist({
            variables: { scheduleItemAttendances: entryIds },
        });
    };

    const handleBulkCancel = async (entryIds) => {
        await bulkCancelWaitlist({
            variables: { scheduleItemAttendances: entryIds },
        });
    };

    const handleRefresh = () => {
        refetchWaitlist();
    };

    const handleDateChange = (newDate) => {
        setCurrentDate(newDate);
    };

    // Don't render if waitlist feature is disabled
    if (shopFeaturesLoading) {
        return (
            <ClassEditBase
                menuActiveLink="waitlist"
                cardTitle={t("schedule.classes.waitlist.title")}
            >
                <Card.Body>
                    <Dimmer active={true} loader={true} />
                </Card.Body>
            </ClassEditBase>
        );
    }

    if (!waitlistEnabled) {
        return (
            <ClassEditBase
                menuActiveLink="waitlist"
                cardTitle={t("schedule.classes.waitlist.title")}
            >
                <Card.Body>
                    <Alert type="info" icon="info">
                        <h4>
                            {t(
                                "schedule.classes.waitlist.feature_disabled_title",
                            )}
                        </h4>
                        <p>
                            {t(
                                "schedule.classes.waitlist.feature_disabled_message",
                            )}
                        </p>
                    </Alert>
                </Card.Body>
            </ClassEditBase>
        );
    }

    if (waitlistError) {
        return (
            <ClassEditBase
                menuActiveLink="waitlist"
                cardTitle={t("schedule.classes.waitlist.title")}
            >
                <Card.Body>
                    <Alert type="danger" icon="alert-triangle">
                        <h4>{t("general.error_loading")}</h4>
                        <p>{waitlistError.message}</p>
                    </Alert>
                </Card.Body>
            </ClassEditBase>
        );
    }

    return (
        <HasPermissionWrapper
            resource="scheduleitemattendance"
            permission="view"
        >
            <ClassEditBase
                menuActiveLink="waitlist"
                cardTitle={t("schedule.classes.waitlist.title")}
                defaultCard={false}
            >
                <Grid.Row>
                    <Grid.Col>
                        {/* Date selector */}
                        <Card className="mb-4">
                            <Card.Header>
                                <Card.Title>
                                    {t(
                                        "schedule.classes.waitlist.date_selector",
                                    )}
                                </Card.Title>
                            </Card.Header>
                            <Card.Body>
                                <div className="d-flex align-items-center gap-3">
                                    <input
                                        type="date"
                                        value={currentDate}
                                        onChange={(e) =>
                                            handleDateChange(e.target.value)
                                        }
                                        className="form-control"
                                        style={{ width: "auto" }}
                                    />
                                    <span className="text-muted">
                                        {t(
                                            "schedule.classes.waitlist.viewing_date",
                                        )}
                                    </span>
                                </div>
                            </Card.Body>
                        </Card>

                        {/* Class info */}
                        {scheduleClass && (
                            <Card className="mb-4">
                                <Card.Header>
                                    <Card.Title>
                                        {t(
                                            "schedule.classes.waitlist.class_info",
                                        )}
                                    </Card.Title>
                                </Card.Header>
                                <Card.Body>
                                    <Grid.Row>
                                        <Grid.Col md={3}>
                                            <div className="text-center">
                                                <div className="h3 mb-1">
                                                    {scheduleClass.spaces}
                                                </div>
                                                <div className="text-muted small">
                                                    {t("general.spaces")}
                                                </div>
                                            </div>
                                        </Grid.Col>
                                        <Grid.Col md={3}>
                                            <div className="text-center">
                                                <div className="h3 mb-1">
                                                    {scheduleClass.countBooked}
                                                </div>
                                                <div className="text-muted small">
                                                    {t(
                                                        "schedule.classes.booked",
                                                    )}
                                                </div>
                                            </div>
                                        </Grid.Col>
                                        <Grid.Col md={3}>
                                            <div className="text-center">
                                                <div className="h3 mb-1">
                                                    {
                                                        scheduleClass.availableSpacesTotal
                                                    }
                                                </div>
                                                <div className="text-muted small">
                                                    {t(
                                                        "schedule.classes.available_spaces",
                                                    )}
                                                </div>
                                            </div>
                                        </Grid.Col>
                                        <Grid.Col md={3}>
                                            <div className="text-center">
                                                <div className="h3 mb-1">
                                                    <Badge
                                                        color={
                                                            scheduleClass.waitlistCount >
                                                            0
                                                                ? "warning"
                                                                : "success"
                                                        }
                                                    >
                                                        {scheduleClass.waitlistCount ||
                                                            0}
                                                    </Badge>
                                                </div>
                                                <div className="text-muted small">
                                                    {t(
                                                        "schedule.classes.waitlist.on_waitlist",
                                                    )}
                                                </div>
                                            </div>
                                        </Grid.Col>
                                    </Grid.Row>
                                </Card.Body>
                            </Card>
                        )}

                        {/* Waitlist controls */}
                        <HasPermissionWrapper
                            resource="scheduleitemattendance"
                            permission="add"
                        >
                            <WaitlistControls
                                scheduleItem={classId}
                                date={currentDate}
                                waitlistEntries={waitlistEntries}
                                onBulkPromote={handleBulkPromote}
                                onBulkCancel={handleBulkCancel}
                                onRefresh={handleRefresh}
                                isLoading={isLoading}
                                canManageWaitlist={true}
                            />
                        </HasPermissionWrapper>

                        {/* Waitlist entries table */}
                        <Card>
                            <Card.Header>
                                <Card.Title>
                                    {t(
                                        "schedule.classes.waitlist.entries_title",
                                    )}
                                    {scheduleClass?.waitlistCount > 0 && (
                                        <Badge
                                            color="secondary"
                                            className="ms-2"
                                        >
                                            {scheduleClass.waitlistCount}
                                        </Badge>
                                    )}
                                </Card.Title>
                            </Card.Header>
                            <Card.Body>
                                {waitlistLoading ? (
                                    <Dimmer active={true} loader={true} />
                                ) : waitlistEntries.length === 0 ? (
                                    <Alert type="info" icon="info">
                                        <p className="mb-0">
                                            {t(
                                                "schedule.classes.waitlist.no_entries",
                                            )}
                                        </p>
                                    </Alert>
                                ) : (
                                    <Table responsive>
                                        <Table.Header>
                                            <Table.Row>
                                                <Table.ColHeader>
                                                    {t(
                                                        "schedule.classes.waitlist.customer",
                                                    )}
                                                </Table.ColHeader>
                                                <Table.ColHeader>
                                                    {t(
                                                        "schedule.classes.waitlist.attendance_type",
                                                    )}
                                                </Table.ColHeader>
                                                <Table.ColHeader>
                                                    {t(
                                                        "schedule.classes.waitlist.joined_at",
                                                    )}
                                                </Table.ColHeader>
                                                <Table.ColHeader>
                                                    {t(
                                                        "schedule.classes.waitlist.waiting_time",
                                                    )}
                                                </Table.ColHeader>
                                                <Table.ColHeader>
                                                    {t("general.actions")}
                                                </Table.ColHeader>
                                            </Table.Row>
                                        </Table.Header>
                                        <Table.Body>
                                            {waitlistEntries.map((entry) => (
                                                <HasPermissionWrapper
                                                    key={entry.id}
                                                    resource="scheduleitemattendance"
                                                    permission="change"
                                                    fallback={
                                                        <WaitlistEntryRow
                                                            entry={entry}
                                                            onPromote={
                                                                handlePromote
                                                            }
                                                            onRemove={
                                                                handleCancel
                                                            }
                                                            isLoading={
                                                                isLoading
                                                            }
                                                            canManageWaitlist={
                                                                false
                                                            }
                                                        />
                                                    }
                                                >
                                                    <WaitlistEntryRow
                                                        entry={entry}
                                                        onPromote={
                                                            handlePromote
                                                        }
                                                        onRemove={handleCancel}
                                                        isLoading={isLoading}
                                                        canManageWaitlist={true}
                                                    />
                                                </HasPermissionWrapper>
                                            ))}
                                        </Table.Body>
                                    </Table>
                                )}
                            </Card.Body>
                        </Card>
                    </Grid.Col>
                </Grid.Row>
            </ClassEditBase>
        </HasPermissionWrapper>
    );
};

export default withTranslation()(withRouter(ClassWaitlistPage));
